import React, { useState } from 'react';
import { ArrowLeftIcon, HeartIcon, PenLineIcon } from 'lucide-react';
import { InitialAvatar } from '../../components/InitialAvatar';
import { Button } from '../../components/ui/Button';
import { Field } from '../../components/ui/Field';
import { SectionCard } from '../../components/ui/SectionCard';
import { TextArea } from '../../components/ui/TextControl';
import { YearsRibbon } from '../../components/profile/YearsRibbon';
import { BadgeMark } from '../../components/profile/MemberBadges';
import { badgesFor } from '../../data/badges';
import { ArchiveTile } from '../../components/archive/ArchiveTile';
import { ArchiveDetail } from '../../components/archive/ArchiveDetail';
import { PrototypeNote } from '../../components/prototype/PrototypeNote';
import { prototypeStateFor } from '../../data/prototypeStates';
import { AFFILIATION_LABELS, CURRENT_MEMBER, overlapWith, publicName, tributesFor } from '../../data/community';
import type { Member } from '../../data/community';
import { ARCHIVE_ITEMS } from '../../data/archive';
import type { ArchiveItem } from '../../data/archive';
import { WORDMARK } from '../../data/registration';
interface MemorialProfileProps {
  member: Member;
  onBack: () => void;
}

/**
 * A profile for someone who has died. Same page, quieted: no wave, no editing,
 * no visibility controls. The circumstances of the death are deliberately not
 * described — only that it happened, and who confirmed it.
 */
export function MemorialProfile({
  member,
  onBack
}: MemorialProfileProps) {
  const [writing, setWriting] = useState(false);
  const [tributeText, setTributeText] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [openItem, setOpenItem] = useState<ArchiveItem | null>(null);
  const memorial = member.memorial;
  const overlap = overlapWith(member, CURRENT_MEMBER);
  const tributes = tributesFor(member.name);
  const contributions = ARCHIVE_ITEMS.filter(item => item.credit.includes(publicName(member.name)));
  const firstName = member.name.split(' ')[0];
  if (!memorial) return null;
  return <div className="min-h-full bg-canvas" data-id="element-687" data-mp="9jrfy4akh54d">
      <header className="bg-ink" data-id="element-688" data-mp="9kssyeb581oq">
        <div className="mx-auto w-full max-w-4xl px-4 py-8 sm:px-6" data-id="element-689" data-mp="9hofeqbsftle">
          <button type="button" onClick={onBack} className="inline-flex min-h-tap items-center gap-2 rounded-control text-small text-on-ink opacity-70 transition-opacity hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-highlight" data-id="element-690" data-mp="nch0e4ag61zd">
            <ArrowLeftIcon aria-hidden="true" className="h-4 w-4" data-id="element-691" data-mp="sc3f6i5xbqaa" />
            Back to people
          </button>

          <div className="mt-5 flex flex-col gap-4 sm:flex-row sm:items-center" data-id="element-692" data-mp="167fk1idsmx4x">
            <InitialAvatar name={member.name} size={72} data-id="element-693" data-mp="1262df62vmpwy" />
            <div className="flex flex-col gap-1" data-id="element-694" data-mp="380d9r5c9x9ln">
              <span className="flex items-center gap-2 text-eyebrow-caption font-semibold uppercase text-highlight" data-id="element-695" data-mp="382ybzvghhrjx">
                <span aria-hidden="true" className="h-px w-5 bg-highlight" data-id="element-696" data-mp="2tvv0eq37q5dn" />
                In memory
              </span>
              <h1 className="text-page-title font-semibold text-on-ink" data-id="element-697" data-mp="2xxga62kga3jm">
                {publicName(member.name)}
              </h1>
              {badgesFor(member.name).length > 0 ? <ul className="flex flex-wrap gap-1.5 pt-0.5" data-id="element-698" data-mp="2p4cxhwhmpuq1">
                  {badgesFor(member.name).map(badge => <li key={badge.key} className="inline-flex items-center gap-1.5 rounded-pill bg-on-ink px-2.5 py-1 text-eyebrow-caption font-semibold uppercase text-ink" data-id="element-699" data-mp="2a6ucn582o1nv">
                      <badge.icon aria-hidden="true" className="h-3.5 w-3.5" data-id="element-700" data-mp="219a91pmqlta3" />
                      {badge.label}
                    </li>)}
                </ul> : null}
              {prototypeStateFor(member.name) ? <PrototypeNote className="mt-1 w-fit border-on-ink text-on-ink opacity-70" data-id="element-701" data-mp="4dmabmrao0jb">
                  {prototypeStateFor(member.name)!.label}
                </PrototypeNote> : null}
              <p className="text-body text-on-ink opacity-80" data-id="element-702" data-mp="1vk0kibqv5f6c">
                {member.years} · {AFFILIATION_LABELS[member.affiliation]}
                {member.city ? ` · ${member.city}` : ''}
              </p>
            </div>
          </div>

          <p className="mt-5 text-body text-on-ink opacity-80" data-id="element-703" data-mp="1mkdf7ev0jp80">
            {memorial.notice} {memorial.confirmedBy}.
          </p>
        </div>
      </header>

      <div className="mx-auto flex w-full max-w-4xl flex-col gap-4 px-4 py-6 sm:px-6" data-id="element-704" data-mp="1crfxvc1rc6az">
        {memorial.givingNote ? <SectionCard data-id="element-705" data-mp="17r1y0hab82o1">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between" data-id="element-706" data-mp="12u1nnu6fgdr4">
              <div className="flex max-w-xl flex-col gap-1.5" data-id="element-707" data-mp="ysgfun8r2pns">
                <h2 className="text-heading font-semibold text-ink" data-id="element-708" data-mp="yrn75gq8dq30">{memorial.givingNote}</h2>
                <p className="text-small text-ink-muted" data-id="element-709" data-mp="ttvgsr58ic0x">
                  Gifts go to {WORDMARK} and are recorded in {publicName(member.name)}&apos;s
                  name. The family is told who gave, never how much.
                </p>
              </div>
              <Button className="shrink-0" data-id="element-710" data-mp="3egverdy74tb4">
                <HeartIcon aria-hidden="true" className="h-4 w-4" data-id="element-711" data-mp="3ei6jph3do73t" />
                Give in their memory
              </Button>
            </div>
          </SectionCard> : null}

        <SectionCard title="Their years here" meta={overlap ? `You were both here ${overlap.start}–${overlap.end}` : undefined} data-id="element-712" data-mp="2mrioelodxvy2">
          <YearsRibbon member={member} compare data-id="element-713" data-mp="2vr5l4u666dks" />
          <p className="mt-4 text-small text-ink-muted" data-id="element-714" data-mp="2vpcdbbjtlnb7">
            {publicName(member.name)} stays on the timeline and in the directory. Nothing they
            added to the archive is removed.
          </p>
        </SectionCard>

        {member.about ? <SectionCard title="In their own words" data-id="element-715" data-mp="280jglcvdjbfj">
            <blockquote className="border-l-2 border-rule pl-4" data-id="element-716" data-mp="27z8b98ma5vmb">
              <p className="max-w-2xl text-body text-ink" data-id="element-717" data-mp="3baxmeusq4he5">{member.about}</p>
              <footer className="mt-2 text-small text-ink-muted" data-id="element-718" data-mp="36dnkwgs28f9y">
                Written by {publicName(member.name)} when they joined, {member.joined}. Left as
                they wrote it.
              </footer>
            </blockquote>
          </SectionCard> : null}

        <SectionCard title="What people have said" meta={`${tributes.length} ${tributes.length === 1 ? 'note' : 'notes'}`} data-id="element-719" data-mp="2sdcsh9b3qsmo">
          <ul className="flex flex-col divide-y divide-rule" data-id="element-720" data-mp="2nfulvpkk4phu">
            {tributes.map(tribute => <li key={`${tribute.from}-${tribute.when}`} className="flex flex-col gap-2 py-4 first:pt-0 last:pb-0" data-id="element-721" data-mp="31fcekg0z4mwh">
                <p className="max-w-2xl text-body text-ink" data-id="element-722" data-mp="gn0tc6p48rn5">{tribute.body}</p>
                <p className="flex flex-wrap items-center gap-1.5 text-small text-ink-muted" data-id="element-723" data-mp="boz93ifyema3">
                  <BadgeMark name={tribute.from} data-id="element-724" data-mp="k5zmzvk12lk5" />
                  {tribute.from} · {tribute.years} · {tribute.when}
                </p>
              </li>)}
          </ul>

          {submitted ? <p className="mt-5 rounded-field bg-primary-soft px-4 py-3 text-small text-on-primary-soft" data-id="element-725" data-mp="a83bv3r3bqm1">
              Thank you. A staff member reads every note before it appears here, usually within a
              day.
            </p> : writing ? <div className="mt-5 flex flex-col gap-4 rounded-field border border-rule p-4" data-id="element-726" data-mp="12ylds1g2vlxv">
              <Field name="tribute" label={`Something you remember about ${firstName}`} hint="It will appear as your first name and last initial, or without a name if you prefer." data-id="element-727" data-mp="17xer9tvd7cy1">
                <TextArea id="tribute" rows={4} value={tributeText} placeholder="He is the reason I came back the second week…" onChange={event => setTributeText(event.target.value)} data-id="element-728" data-mp="1lutmjf8auexx" />
              </Field>
              <div className="flex flex-wrap items-center gap-2" data-id="element-729" data-mp="3hx6rc9jsi7tt">
                <Button onClick={() => setSubmitted(true)} data-id="element-730" data-mp="3cyvia11bk1cn">Add your note</Button>
                <Button variant="quiet" onClick={() => setWriting(false)} data-id="element-731" data-mp="3cwshclymont5">
                  Cancel
                </Button>
                <span className="text-small text-ink-muted" data-id="element-732" data-mp="9iy65mjlkjbo">
                  Read by staff, and by {firstName}&apos;s family, before it appears.
                </span>
              </div>
            </div> : <Button variant="secondary" className="mt-5" onClick={() => setWriting(true)} data-id="element-733" data-mp="goyd4fpr41z">
              <PenLineIcon aria-hidden="true" className="h-4 w-4" data-id="element-734" data-mp="3roucjspr6smu" />
              Add something you remember
            </Button>}
        </SectionCard>

        {contributions.length > 0 ? <SectionCard title="What they added to the archive" meta={`${contributions.length} ${contributions.length === 1 ? 'item' : 'items'}`} data-id="element-735" data-mp="2g7umz4usirwi">
            <ul className="columns-1 gap-4 sm:columns-2 [&>li]:mb-4" data-id="element-736" data-mp="10peupft2lrfv">
              {contributions.map(item => <ArchiveTile key={item.id} item={item} onOpen={setOpenItem} data-id="element-737" data-mp="14qgxg6m74lml" />)}
            </ul>
          </SectionCard> : null}

        <p className="px-1 pb-4 text-small text-ink-muted" data-id="element-738" data-mp="19k30r86gzd5q">
          This page is looked after by the portal team. If something here is wrong, or the family
          would like it changed or taken down, write to us and a person will answer — not a form.
        </p>
      </div>

      <ArchiveDetail item={openItem} onClose={() => setOpenItem(null)} data-id="element-739" data-mp="1ogahtzmkzygs" />
    </div>;
}